import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ToastrService } from '@shared/services/toastr.service';
import { AuthService, MFA } from '@shared/services/auth.service';
import { DEFAULT_LANG, Lang, SUPPORTED_LANGUAGES } from '@shared/utils/lang';
import { MyProfileService, UpdateProfileRequest, UserProfile } from '@shared/services/my-profile.service';
import { Unsubscribe } from '@shared/decorator/unsubscribe';
import { NgClass, NgIf } from '@angular/common';
import { LabelComponent } from '@shared/components/label/label.component';
import { TranslatePipe } from '@ngx-translate/core';

@Unsubscribe
@Component({
  selector: 'app-my-parameters',
  templateUrl: './my-parameters.component.html',
  styleUrls: ['./my-parameters.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, NgIf, LabelComponent, NgClass, TranslatePipe],
})
export class MyParametersComponent implements OnInit {
  userFormGroup: FormGroup<FGUserProfile>;
  initialValues: UpdateProfileRequest;
  errors: Record<keyof Partial<FGUserProfile>, Record<string, string>> = {
    firstName: {
      required: 'FieldRequired',
    },
    lastName: {
      required: 'FieldRequired',
    },
    lang: {
      required: 'FieldRequired',
      minLength: 'TooShort',
    },
    email: {
      required: 'FieldRequired',
      email: 'InvalidEmail',
    },
    mfa: {},
  };

  constructor(
    private auth: AuthService,
    private profileService: MyProfileService,
    private toastr: ToastrService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.getUser();

    this.userFormGroup = this.fb.group({
      firstName: new FormControl('', { validators: [Validators.required] }),
      lastName: new FormControl('', { validators: [Validators.required] }),
      email: new FormControl({ value: '', disabled: true }, { validators: [Validators.required, Validators.email] }),
      lang: new FormControl(DEFAULT_LANG, { validators: [Validators.required, Validators.minLength(2)] }),
      mfa: new FormControl(MFA.NONE, { validators: [] }),
    });
  }

  /**
   * Enable all fields except email
   * @description Email always must disable;
   * @return void
   */
  onEditField = (): void => {
    if (this.userFormGroup.disabled) {
      this.userFormGroup.enable();
      this.userFormGroup.get('email').disable();
    } else {
      this.userFormGroup.disable();
    }
  };

  updateUser = (): void => {
    const data: UpdateProfileRequest = this.userFormGroup.getRawValue();

    this.profileService.updateProfile(data).subscribe({
      next: (): void => {
        this.getUser();
        this.toastr.success('SuccfChang');
        this.userFormGroup.disable();
      },
      error: (): void => {
        this.toastr.error('VeuillezRenseignerTousLesChamps');
      },
    });
  };

  cancelEditUser = (): void => {
    this.userFormGroup.reset(this.initialValues);
    this.userFormGroup.disable();
  };

  getUser(): void {
    this.profileService.getProfile().subscribe((user: UserProfile) => {
      const { hasApiKey, ...data } = user;
      this.initialValues = data;
      this.userFormGroup.setValue(data);
      this.userFormGroup.markAsUntouched();
      this.userFormGroup.markAsPristine();
      this.userFormGroup.disable();
      this.auth.updateUser({ ...user, lang: this.auth.getCurrentLanguage() });
    });
  }

  getErrors(name: keyof FGUserProfile): ValidationErrors {
    return this.userFormGroup.get(name).errors;
  }

  getErrorsMessage(name: keyof FGUserProfile): Array<string> {
    const errors: Array<string> = Object.keys(this.getErrors(name));
    return errors.map((error) => this.errors[name][error]);
  }

  protected readonly SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGES;
  protected readonly Object = Object;
}

export type FGUserProfile = {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  lang: FormControl<Lang>;
  mfa: FormControl<MFA>;
};
